import ModalNote from 'components/Checkout/ProductNoteModal';
import NewMarketItem from 'components/MarketItem/NewMarketItem';
import { Viewport } from 'components/ViewportProvider';
import { selectAddedProducts, selectAddingProducts, selectUserPermissions } from 'containers/MainLayout/selectors';
import { AddedProduct, AddingProduct } from 'containers/MainLayout/types';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styles/styled-components';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ApplicationRootState } from 'types';
import { Product, TrackingAddToCartAddFromType, TrackingProductImpressionType } from 'types/schema';
import utilMessages from 'utils/messages';
import CheckIcon from '../../../components/Product/_icons/CheckIcon.svg';
import SVGIcon from '../../../components/SVGIcon/SVGIcon';
import { kamereoItemFavorite, kamereoItemSetNote, setViewMore } from '../actions';
import selectBrandDetailPage, { selectLoading, selectNoteItemLoading } from '../selectors';
import KamereoProductBackground from './KamereoLogo.svg';
import { ContainerState } from './types';
import { setOpenSigninPopup } from 'containers/MainLayout/actions';

const PromotionWrapper = styled.div`
  background: linear-gradient(295deg, #e3ffe4 8.83%, #f0fff0 92.39%);
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  margin-top: 40px;
  @media (max-width: 991px) {
    margin: 16px -16px 0;
    border-radius: 0px;
    margin-top: 16px;
  }
`;

const KamereoBackgroundImage = styled.div`
  position: absolute;
  top: 20px;
  right: 64px;
  z-index: 0;
  @media (max-width: 991px) {
    top: 25px;
    right: -40px;
    opacity: 0.7;
    & img {
      width: 120px;
    }
  }
`;

const PromotionCornerTitle = styled.div`
  background: #f38a3e;
  padding: 6px 12px;
  display: flex;
  gap: 4px;
  width: fit-content;
  border-radius: 6px;
`;

const PromotionCornerTitleText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
`;

const PromotionTitleWrapper = styled.div`
  margin: 13px 16px 20px;
  position: relative;
`;

const PromotionTitleBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PromotionTitleText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #2f382c;
`;

const PromotionTitleSubText = styled.div`
  font-size: 14px;
  color: #596056;
`;

const PromotionKamereoProductList = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  margin: 0 16px;
  overflow: auto;
  gap: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  overflow: auto;
  @media (max-width: 991px) {
    gap: 8px;
    margin: 0;
  }
`;

const SwiperElement = styled(Swiper)`
  max-width: 1136px;
  margin: 0px 0px 33px !important;
  padding: 0 1px 1px !important;
  .swiper-button-next,
  .swiper-button-prev {
    top: 50% !important;
    transform: translateY(-50%);
    width: 30px !important;
    height: 30px !important;
    border-radius: 50% !important;
    background-color: #ffffff !important;
    box-shadow: 0px 0px 6.6px 0px rgba(0, 0, 0, 0.12);
    border: 1px solid #d5d7d5;
    &:after {
      font-size: 14px !important;
      color: #596056 !important;
    }
    &.swiper-button-disabled {
      display: none !important;
    }
  }
  .swiper-button-next {
    right: 4px;

    &:after {
      margin-left: 1px;
    }
  }
  .swiper-button-prev {
    left: 4px;
    &:after {
      margin-right: 1px;
    }
  }
  @media (max-width: 767px) {
    .swiper-button-next,
    .swiper-button-prev {
      display: none !important;
    }
    margin-bottom: 12px !important;
  }
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: 221px !important;
  @media (max-width: 991px) {
    width: 170px !important;
    &:first-child {
      margin-left: 16px;
    }
    &:last-child {
      margin-right: 16px;
    }
  }
  & > div > article > div > div {
    padding: 0px;
  }
  & > div > article > div > div > div > div {
    padding: 8px;
  }
`;

const Item = styled('div')`
  flex: 0 0 180px;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 1px solid #eaebea;
  overflow: hidden;
`;

interface IStateProps {
  brandDetailDetail: ContainerState;
  loading: boolean;
  addedProducts: AddedProduct;
  addingProducts: AddingProduct;
  permissions: string[];
  noteItemLoading: boolean;
}

interface IToggleFavorite {
  brandId: number;
  product: Product;
  index: number;
}

interface IProductNoteParams {
  brandId: number;
  product: Product;
  index: number;
}

interface IDispatchProps {
  onToggleFavorite: (data: IToggleFavorite) => void;
  onSetProductNote: (data: IProductNoteParams) => void;
  gotoSignIn: () => void;
  onViewMore: (data: { brandId: string; page: number }) => void;
}

interface IOwnProps {
  brandId: number;
}

type PropsType = IStateProps & IDispatchProps & IOwnProps;

export const KamereoProductList: FC<PropsType> = ({
  brandId,
  brandDetailDetail: { kamereoProductsInSameProductGroups },
  permissions,
  noteItemLoading,
  gotoSignIn,
  onToggleFavorite,
  onSetProductNote,
}) => {
  const loggedIn = !!localStorage.getItem('token');
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);

  const products = useMemo(() => {
    if (kamereoProductsInSameProductGroups?.[brandId]?.products?.length) {
      return kamereoProductsInSameProductGroups?.[brandId]?.products;
    }
    return [];
  }, [kamereoProductsInSameProductGroups?.[brandId]]);

  const onClickOpenNoteModal = useCallback(
    (product: Product, index: number) => {
      if (loggedIn) {
        setIsOpenNoteModal(true);
        setSelectedProduct({
          index: index,
          brandId: brandId,
          product: product,
        });
      } else {
        gotoSignIn();
      }
    },
    [setSelectedProduct, setIsOpenNoteModal, loggedIn],
  );

  const onClickCloseNoteModal = useCallback(() => {
    setSelectedProduct(undefined);
    setIsOpenNoteModal(false);
  }, [setSelectedProduct, setIsOpenNoteModal]);

  const onSubmitAddingNote = useCallback(
    (note: string) => {
      if (selectedProduct) {
        onSetProductNote({
          index: selectedProduct.index,
          brandId: selectedProduct.brandId,
          product: {
            ...selectedProduct.product,
            note: note,
          },
        });
      }
      onClickCloseNoteModal();
    },
    [selectedProduct, onClickCloseNoteModal],
  );
  if (!products.length) {
    return null;
  }
  return (
    <PromotionWrapper>
      <KamereoBackgroundImage>
        <SVGIcon src={KamereoProductBackground} />
      </KamereoBackgroundImage>
      <PromotionTitleWrapper>
        <PromotionTitleBox>
          <PromotionCornerTitle>
            <SVGIcon src={CheckIcon} />
            <PromotionCornerTitleText>
              <FormattedMessage {...utilMessages.promotionKamereo} />
            </PromotionCornerTitleText>
          </PromotionCornerTitle>
          <PromotionTitleText>
            <FormattedMessage {...utilMessages.promotionKamereoTitle} />
          </PromotionTitleText>
        </PromotionTitleBox>
        <PromotionTitleSubText>
          <FormattedMessage {...utilMessages.promotionKamereoSubTitle} />
        </PromotionTitleSubText>
      </PromotionTitleWrapper>
      <Viewport.Consumer>
        {({ width }) => {
          return (
            <PromotionKamereoProductList>
              <SwiperElement
                slidesPerView={'auto'}
                spaceBetween={width <= 991 ? 8 : 16}
                navigation={true}
                modules={[Navigation, Mousewheel]}
                mousewheel={{ forceToAxis: true }}
                simulateTouch={true}
                className="mySwiper"
                slidesPerGroup={2}
                autoPlay={false}
              >
                {(products as Product[]).map((product: Product, index: number) => (
                  <SwiperSlideElement key={product?.id}>
                    <Item>
                      <NewMarketItem
                        vertialOnly
                        listName=""
                        loggedIn={loggedIn}
                        gotoSignIn={gotoSignIn}
                        product={product}
                        loading={false}
                        favorite={product.isFavorite}
                        note={product.note}
                        added={0}
                        adding={false}
                        permissions={permissions}
                        onToggleFavorite={() => onToggleFavorite({ product: product, index: index, brandId: brandId })}
                        onOpenNote={() => onClickOpenNoteModal(product, index)}
                        onDeleteNote={() => {
                          onSetProductNote({
                            index: index,
                            brandId: brandId,
                            product: {
                              ...product,
                              note: '',
                            },
                          });
                          onClickCloseNoteModal();
                        }}
                        impressionProperties={{ type: TrackingProductImpressionType.CartProduct }}
                        addToCartProperties={{ addFrom: TrackingAddToCartAddFromType.CartProduct }}
                      />
                    </Item>
                  </SwiperSlideElement>
                ))}
              </SwiperElement>
            </PromotionKamereoProductList>
          );
        }}
      </Viewport.Consumer>
      <ModalNote
        headerLabel={selectedProduct?.name || ''}
        isOpen={isOpenNoteModal}
        confirmLoading={noteItemLoading}
        onSubmit={onSubmitAddingNote}
        onClose={onClickCloseNoteModal}
        note={selectedProduct?.product?.note || ''}
      />
    </PromotionWrapper>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  brandDetailDetail: selectBrandDetailPage(),
  loading: selectLoading(),
  addedProducts: selectAddedProducts(),
  addingProducts: selectAddingProducts(),
  permissions: selectUserPermissions(),
  noteItemLoading: selectNoteItemLoading(),
});
const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  onToggleFavorite: (data: IToggleFavorite) => dispatch(kamereoItemFavorite.request(data)),
  onSetProductNote: (data: IProductNoteParams) => dispatch(kamereoItemSetNote.request(data)),
  gotoSignIn: () => dispatch(setOpenSigninPopup(true)),
  onViewMore: (data: { brandId: string; page: number }) => dispatch(setViewMore(data)),
});

const withConnect = connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps);

export default withConnect(KamereoProductList);
